import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { fetchTokenPrices, estimateMarketCap } from '../../services/binancePriceService';
import './EcosystemResponsive.css';

interface Token {
  id: string;
  name: string;
  symbol: string;
  price: number;
  change24h: number;
  change7d: number;
  volume24h: number;
  marketCap: number;
  icon: string;
  category: 'stablecoin' | 'defi' | 'wrapped' | 'governance' | 'other';
  isFavorite: boolean;
  lastUpdate: Date;
  coingeckoId?: string;
}

interface FilterOptions {
  category: string;
  priceMin: number | null;
  priceMax: number | null;
  marketCapMin: number | null;
  marketCapMax: number | null;
  change24hMin: number | null;
  change24hMax: number | null;
}

interface ColumnVisibility {
  token: boolean;
  price: boolean;
  change24h: boolean;
  volume24h: boolean;
  marketCap: boolean;
}

const EcosystemResponsive: React.FC = () => {
  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  const [activeTab, setActiveTab] = useState<'tokens' | 'protocols'>('tokens');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('All Networks');
  const [sortBy, setSortBy] = useState<string>('marketCap');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterOptions>({
    category: 'all',
    priceMin: null,
    priceMax: null,
    marketCapMin: null,
    marketCapMax: null,
    change24hMin: null,
    change24hMax: null,
  });
  
  // Column visibility for desktop
  // Column visibility state - reserved for future column customization feature
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setShowColumnSettings] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [columnVisibility] = useState<ColumnVisibility>({
    token: true,
    price: true,
    change24h: true,
    volume24h: true,
    marketCap: true,
  });

  useEffect(() => {
    // Mock data that matches the reference image
    const mockTokens: Token[] = [
      {
        id: 'usdp',
        name: 'USDP Bridged E-Pay Tether',
        symbol: 'USDP',
        price: 1.00,
        change24h: 0.08,
        change7d: 0.00,
        volume24h: 6283661,
        marketCap: 1052000000,
        icon: '/USDPlogo.png',
        category: 'stablecoin',
        isFavorite: true,
        lastUpdate: new Date(),
        coingeckoId: 'paxos-standard'
      },
      {
        id: 'tether',
        name: 'Tether USD',
        symbol: 'USDT',
        price: 1.00,
        change24h: -0.01,
        change7d: 0.02,
        volume24h: 500500000,
        marketCap: 86800000000,
        icon: '/icons/USDT.svg',
        category: 'stablecoin',
        isFavorite: false,
        lastUpdate: new Date(),
        coingeckoId: 'tether'
      },
      {
        id: 'usd-coin',
        name: 'USD Coin',
        symbol: 'USDC',
        price: 1.000,
        change24h: 0.01,
        change7d: -0.01,
        volume24h: 391400000,
        marketCap: 48080000000,
        icon: '/icons/USDC.svg',
        category: 'stablecoin',
        isFavorite: true,
        lastUpdate: new Date(),
        coingeckoId: 'usd-coin'
      },
      {
        id: 'steth',
        name: 'Liquid staked Ether 2.0',
        symbol: 'stETH',
        price: 3254.70,
        change24h: -9.48,
        change7d: 3.24,
        volume24h: 40800230,
        marketCap: 27094000000,
        icon: '/icons/steth.svg',
        category: 'defi',
        isFavorite: false,
        lastUpdate: new Date(),
        coingeckoId: 'staked-ether'
      },
      {
        id: 'dai',
        name: 'Dai Stablecoin',
        symbol: 'DAI',
        price: 1.00,
        change24h: 0.00,
        change7d: 0.01,
        volume24h: 159811921,
        marketCap: 4628709183,
        icon: '/icons/dai.svg',
        category: 'stablecoin',
        isFavorite: false,
        lastUpdate: new Date(),
        coingeckoId: 'dai'
      },
      {
        id: 'curve',
        name: 'Curve DAO Token',
        symbol: 'CRV',
        price: 0.4273,
        change24h: 0.12,
        change7d: 0.00,
        volume24h: 196711341,
        marketCap: 5607292561,
        icon: '/icons/crv.svg',
        category: 'defi',
        isFavorite: false,
        lastUpdate: new Date(),
        coingeckoId: 'curve-dao-token'
      },
      {
        id: 'chainlink',
        name: 'ChainLink Token',
        symbol: 'LINK',
        price: 14.72,
        change24h: -3.4,
        change7d: 0.00,
        volume24h: 3763092,
        marketCap: 10261277861,
        icon: '/icons/LINK.svg',
        category: 'defi',
        isFavorite: true,
        lastUpdate: new Date(),
        coingeckoId: 'chainlink'
      },
      {
        id: 'wbtc',
        name: 'Wrapped BTC',
        symbol: 'WBTC',
        price: 101270,
        change24h: -4.9,
        change7d: 0.00,
        volume24h: 926781455,
        marketCap: 12851492759,
        icon: '/icons/btc.svg',
        category: 'wrapped',
        isFavorite: false,
        lastUpdate: new Date(),
        coingeckoId: 'wrapped-bitcoin'
      },
      {
        id: 'busd',
        name: 'Binance USD',
        symbol: 'BUSD',
        price: 0.998,
        change24h: -0.10,
        change7d: 0.00,
        volume24h: 9912343,
        marketCap: 310429109,
        icon: '/icons/busd.svg',
        category: 'stablecoin',
        isFavorite: false,
        lastUpdate: new Date(),
        coingeckoId: 'binance-usd'
      },
    ];

    const loadTokens = async () => {
      setLoading(true);
      
      // Get tokens with coingeckoId
      const tokensWithId = mockTokens.filter(token => token.coingeckoId);
      const coingeckoIds = tokensWithId.map(token => token.coingeckoId!);
      
      let realData: any = {};
      if (coingeckoIds.length > 0) {
        realData = await fetchTokenPrices(coingeckoIds);
      }

      // Update tokens with real data
      const updatedTokens = mockTokens.map(token => {
        if (token.coingeckoId && realData[token.coingeckoId]) {
          const data = realData[token.coingeckoId];
          return {
            ...token,
            price: data.usd,
            change24h: data.usd_24h_change,
            volume24h: data.total_volume,
            marketCap: data.market_cap || estimateMarketCap(token.coingeckoId!, data.usd),
          };
        }
        return token;
      });

      setTokens(updatedTokens);
      setLoading(false);
    };

    loadTokens();

    // Real-time price updates
    const priceUpdateInterval = setInterval(async () => {
      const tokensWithId = mockTokens.filter(token => token.coingeckoId);
      const coingeckoIds = tokensWithId.map(token => token.coingeckoId!);

      if (coingeckoIds.length > 0) {
        const realData = await fetchTokenPrices(coingeckoIds);
        setTokens(prevTokens =>
          prevTokens.map(token => {
            if (token.coingeckoId && realData[token.coingeckoId]) {
              const data = realData[token.coingeckoId];
              return {
                ...token,
                price: data.usd,
                change24h: data.usd_24h_change,
                volume24h: data.total_volume,
                marketCap: data.market_cap || estimateMarketCap(token.coingeckoId!, data.usd),
                lastUpdate: new Date()
              };
            }
            return token;
          })
        );
      }
    }, 60000); // Update every 1 minute

    return () => clearInterval(priceUpdateInterval);
  }, []);

  // Formatting functions
  const formatPrice = (price: number): string => {
    if (price >= 1000) {
      return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    } else if (price >= 1) {
      return `$${price.toFixed(3)}`;
    } else {
      return `$${price.toFixed(6)}`;
    }
  };

  const formatVolume = (volume: number): string => {
    if (volume >= 1000000000) {
      return `$${(volume / 1000000000).toFixed(2)}B`;
    } else if (volume >= 1000000) {
      return `$${(volume / 1000000).toFixed(1)}M`;
    } else if (volume >= 1000) {
      return `$${(volume / 1000).toFixed(1)}K`;
    }
    return `$${volume.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatMarketCap = (marketCap: number): string => {
    if (marketCap >= 1000000000000) {
      return `$${(marketCap / 1000000000000).toFixed(2)}T`;
    } else if (marketCap >= 1000000000) {
      return `$${(marketCap / 1000000000).toFixed(2)}B`;
    } else if (marketCap >= 1000000) {
      return `$${(marketCap / 1000000).toFixed(1)}M`;
    }
    return `$${marketCap.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatChange = (change: number): string => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}%`;
  };

  // Filter functions
  const handleQuickFilter = (filter: string) => {
    setActiveQuickFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const handleFavoriteToggle = (tokenId: string) => {
    setTokens(prevTokens =>
      prevTokens.map(token =>
        token.id === tokenId 
          ? { ...token, isFavorite: !token.isFavorite }
          : token
      )
    );
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Apply filters and sorting
  const filteredTokens = tokens.filter(token => {
    const matchesSearch = searchQuery === '' ||
                         token.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         token.symbol.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = filters.category === 'all' || token.category === filters.category;
    
    const matchesQuickFilters = activeQuickFilters.length === 0 || 
                               activeQuickFilters.includes(token.category) ||
                               (activeQuickFilters.includes('favorites') && token.isFavorite);
    
    const matchesPrice = (!filters.priceMin || token.price >= filters.priceMin) &&
                        (!filters.priceMax || token.price <= filters.priceMax);
    
    const matchesMarketCap = (!filters.marketCapMin || token.marketCap >= filters.marketCapMin) &&
                            (!filters.marketCapMax || token.marketCap <= filters.marketCapMax);
    
    const matchesChange = (!filters.change24hMin || token.change24h >= filters.change24hMin) &&
                         (!filters.change24hMax || token.change24h <= filters.change24hMax);
    
    return matchesSearch && matchesCategory && matchesQuickFilters && matchesPrice && matchesMarketCap && matchesChange;
  });

  const sortedTokens = [...filteredTokens].sort((a, b) => {
    // USDP tokens always at top unless specific sorting
    if (sortBy === 'marketCap' || sortBy === 'volume24h' || sortBy === 'price' || sortBy === 'change24h') {
      // Allow normal sorting
    } else {
      if (a.id === 'usdp') return -1;
      if (b.id === 'usdp') return 1;
    }

    const aValue = a[sortBy as keyof Token];
    const bValue = b[sortBy as keyof Token];
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return sortOrder === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  // Mobile Card Component
  const TokenCard: React.FC<{ token: Token; index: number }> = ({ token, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`token-card-mobile ${token.isFavorite ? 'favorite' : ''}`}
      onClick={() => {/* Handle token selection */}}
    >
      <div className={`category-badge-mobile ${token.category}`}>
        {token.category}
      </div>
      
      <div className="token-card-header">
        <div className="token-info-primary">
          <div className="token-icon-mobile">
            <img src={token.icon} alt={token.symbol} onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!.textContent = token.symbol.slice(0, 2);
            }} />
          </div>
          <div className="token-name-mobile">
            <h3 className="token-symbol-mobile">{token.symbol}</h3>
            <p className="token-name-text">{token.name}</p>
          </div>
        </div>
        <button 
          className={`token-favorite-mobile ${token.isFavorite ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleFavoriteToggle(token.id);
          }}
          aria-label={`${token.isFavorite ? 'Remove from' : 'Add to'} favorites`}
        >
          {token.isFavorite ? '★' : '☆'}
        </button>
      </div>

      <div className="token-card-content">
        <div className="token-metric">
          <span className="token-metric-label">Price</span>
          <span className="token-metric-value">{formatPrice(token.price)}</span>
        </div>
        
        <div className="token-metric">
          <span className="token-metric-label">24h Change</span>
          <span className={`token-metric-change ${token.change24h > 0 ? 'positive' : token.change24h < 0 ? 'negative' : 'neutral'}`}>
            {formatChange(token.change24h)}
          </span>
        </div>
        
        <div className="token-metric">
          <span className="token-metric-label">Volume 24h</span>
          <span className="token-metric-value">{formatVolume(token.volume24h)}</span>
        </div>
        
        <div className="token-metric">
          <span className="token-metric-label">Market Cap</span>
          <span className="token-metric-value">{formatMarketCap(token.marketCap)}</span>
        </div>
      </div>
    </motion.div>
  );

  // Loading skeleton for mobile
  const TokenCardSkeleton: React.FC = () => (
    <div className="token-card-skeleton">
      <div className="skeleton-line short"></div>
      <div className="skeleton-line medium"></div>
      <div className="skeleton-line long"></div>
      <div className="skeleton-line short"></div>
    </div>
  );

  return (
    <section className="ecosystem-responsive">
      <div className="container">
        {/* Header */}
        <motion.div
          className="ecosystem-header-responsive"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="ecosystem-title-responsive">
            Growing <span className="title-gradient">Ecosystem</span>
          </h2>
          <p className="ecosystem-subtitle-responsive">
            USDP powers a comprehensive ecosystem of partners, integrations, and developer tools
            enabling the next generation of digital finance applications.
          </p>
        </motion.div>

        {/* Token Explorer */}
        <motion.div
          className="token-explorer-responsive"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Tab Navigation */}
          <div className="tab-navigation-responsive">
            <button
              className={`tab-button-responsive ${activeTab === 'tokens' ? 'active' : ''}`}
              onClick={() => setActiveTab('tokens')}
            >
              Tokens
            </button>
            <button
              className={`tab-button-responsive ${activeTab === 'protocols' ? 'active' : ''}`}
              onClick={() => setActiveTab('protocols')}
            >
              Protocols
            </button>
          </div>

          {activeTab === 'tokens' && (
            <>
              {/* Search and Filters */}
              <div className="search-filters-mobile">
                {/* Search Input */}
                <div className="search-container-mobile">
                  <input
                    type="text"
                    className="search-input-mobile"
                    placeholder="Search tokens by name or symbol..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <svg className="search-icon-mobile" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                    <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>

                {/* Quick Filters */}
                <div className="filter-controls-mobile">
                  <button
                    className={`filter-button-mobile ${activeQuickFilters.includes('favorites') ? 'active' : ''}`}
                    onClick={() => handleQuickFilter('favorites')}
                  >
                    <span>★</span>
                    Favorites
                  </button>
                  <button
                    className={`filter-button-mobile ${activeQuickFilters.includes('stablecoin') ? 'active' : ''}`}
                    onClick={() => handleQuickFilter('stablecoin')}
                  >
                    Stablecoins
                  </button>
                  <button
                    className={`filter-button-mobile ${activeQuickFilters.includes('defi') ? 'active' : ''}`}
                    onClick={() => handleQuickFilter('defi')}
                  >
                    DeFi
                  </button>
                  <button
                    className={`filter-button-mobile ${activeQuickFilters.includes('wrapped') ? 'active' : ''}`}
                    onClick={() => handleQuickFilter('wrapped')}
                  >
                    Wrapped
                  </button>
                  <select
                    value={selectedNetwork}
                    onChange={(e) => setSelectedNetwork(e.target.value)}
                    className="filter-button-mobile"
                    style={{ appearance: 'none', background: 'inherit' }}
                  >
                    <option value="All Networks">Networks</option>
                    <option value="Ethereum">Ethereum</option>
                    <option value="Polygon">Polygon</option>
                    <option value="BSC">BSC</option>
                    <option value="Arbitrum">Arbitrum</option>
                  </select>
                </div>

                {/* Advanced Filters Collapsible */}
                <div className="advanced-filters-mobile">
                  <button
                    className="advanced-filters-toggle"
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  >
                    <span>Advanced Filters</span>
                    <svg 
                      width="12" 
                      height="12" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      style={{ 
                        transform: showAdvancedFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                  
                  <AnimatePresence>
                    {showAdvancedFilters && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="advanced-filters-content expanded"
                      >
                        <div className="filter-group-mobile">
                          <label className="filter-label-mobile">Price Range</label>
                          <div className="filter-range-mobile">
                            <input
                              type="number"
                              className="filter-input-mobile"
                              placeholder="Min"
                              value={filters.priceMin || ''}
                              onChange={(e) => setFilters({...filters, priceMin: e.target.value ? Number(e.target.value) : null})}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              className="filter-input-mobile"
                              placeholder="Max"
                              value={filters.priceMax || ''}
                              onChange={(e) => setFilters({...filters, priceMax: e.target.value ? Number(e.target.value) : null})}
                            />
                          </div>
                        </div>

                        <div className="filter-group-mobile">
                          <label className="filter-label-mobile">Market Cap Range</label>
                          <div className="filter-range-mobile">
                            <input
                              type="number"
                              className="filter-input-mobile"
                              placeholder="Min"
                              value={filters.marketCapMin || ''}
                              onChange={(e) => setFilters({...filters, marketCapMin: e.target.value ? Number(e.target.value) : null})}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              className="filter-input-mobile"
                              placeholder="Max"
                              value={filters.marketCapMax || ''}
                              onChange={(e) => setFilters({...filters, marketCapMax: e.target.value ? Number(e.target.value) : null})}
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Mobile Token Cards */}
              {isMobile && (
                <div className="tokens-container-mobile">
                  <div className="tokens-grid-mobile">
                    {loading ? (
                      Array.from({ length: 5 }).map((_, index) => (
                        <TokenCardSkeleton key={index} />
                      ))
                    ) : (
                      sortedTokens.map((token, index) => (
                        <TokenCard key={token.id} token={token} index={index} />
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Tablet/Desktop Table */}
              {(isTablet || isDesktop) && (
                <div className="tokens-container-tablet">
                  <div className="tokens-table-scroll">
                    <table className="tokens-table-responsive">
                      <thead className="table-header-responsive">
                        <tr>
                          <th style={{ width: '40px' }}>⭐</th>
                          <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Token</th>
                          <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>Price</th>
                          <th onClick={() => handleSort('change24h')} style={{ cursor: 'pointer' }}>24h Change</th>
                          <th onClick={() => handleSort('volume24h')} style={{ cursor: 'pointer' }}>Volume 24h</th>
                          <th onClick={() => handleSort('marketCap')} style={{ cursor: 'pointer' }}>Market Cap</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          Array.from({ length: 10 }).map((_, index) => (
                            <tr key={index} className="table-row-responsive">
                              <td>-</td>
                              <td>Loading...</td>
                              <td>-</td>
                              <td>-</td>
                              <td>-</td>
                              <td>-</td>
                            </tr>
                          ))
                        ) : (
                          sortedTokens.map((token) => (
                            <motion.tr
                              key={token.id}
                              className="table-row-responsive"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.3 }}
                            >
                              <td>
                                <button 
                                  className={`token-favorite-mobile ${token.isFavorite ? 'active' : ''}`}
                                  onClick={() => handleFavoriteToggle(token.id)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                >
                                  {token.isFavorite ? '★' : '☆'}
                                </button>
                              </td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <div className="token-icon-mobile" style={{ width: '32px', height: '32px' }}>
                                    <img src={token.icon} alt={token.symbol} onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                      (e.target as HTMLImageElement).parentElement!.textContent = token.symbol.slice(0, 2);
                                    }} />
                                  </div>
                                  <div>
                                    <div style={{ fontWeight: 'bold', color: '#FFFFFF' }}>{token.symbol}</div>
                                    <div style={{ fontSize: '12px', color: '#8B949E' }}>{token.name}</div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                {formatPrice(token.price)}
                              </td>
                              <td>
                                <span className={`token-metric-change ${token.change24h > 0 ? 'positive' : token.change24h < 0 ? 'negative' : 'neutral'}`}>
                                  {formatChange(token.change24h)}
                                </span>
                              </td>
                              <td style={{ fontFamily: 'monospace' }}>
                                {formatVolume(token.volume24h)}
                              </td>
                              <td style={{ fontFamily: 'monospace' }}>
                                {formatMarketCap(token.marketCap)}
                              </td>
                            </motion.tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default EcosystemResponsive;